import { CartAddEvent } from '@theme/events';

/** @typedef {'all' | 'whisky' | 'tequila' | 'vodka' | 'gin' | 'rum' | 'wine' | 'beer-rtd' | 'mixed'} DrinkCategory */
/** @typedef {'all' | 'under250' | '250to500' | '500to1000' | 'over1000'} Budget */

/**
 * Party bundle finder and product cart actions.
 * All prices and variant IDs are rendered by Shopify Liquid from selected products.
 */
class PartyBundles extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const groupButton = event.target.closest('[data-group]');
    if (groupButton instanceof HTMLButtonElement && this.contains(groupButton)) {
      this.#activate('[data-group]', groupButton);
      this.#render();
      return;
    }

    const categoryButton = event.target.closest('[data-category], [data-type]');
    if (categoryButton instanceof HTMLButtonElement && this.contains(categoryButton)) {
      this.#activate('[data-category], [data-type]', categoryButton);
      this.#render();
      return;
    }

    if (event.target.closest('[data-reset]')) {
      const group = this.querySelector('[data-default-group]') ?? this.querySelector('[data-group]');
      const category = this.querySelector('[data-category="all"], [data-type="all"]');
      const budget = this.querySelector('[data-budget]');
      if (group instanceof HTMLButtonElement) this.#activate('[data-group]', group);
      if (category instanceof HTMLButtonElement) this.#activate('[data-category], [data-type]', category);
      if (budget instanceof HTMLSelectElement) budget.value = 'all';
      this.#render();
      return;
    }

    const aiButton = event.target.closest('[data-ai-help]');
    if (aiButton) {
      event.preventDefault();
      this.#openChat();
      return;
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-budget]')) this.#render();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-cart-form]')) return;
    event.preventDefault();
    void this.#addToCart(event.target);
  };

  /** @param {string} selector @param {HTMLButtonElement} selected */
  #activate(selector, selected) {
    for (const button of this.querySelectorAll(selector)) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const active = button === selected;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  #render() {
    const activeGroup = this.querySelector('[data-group][aria-pressed="true"]') ?? this.querySelector('[data-default-group]') ?? this.querySelector('[data-group]');
    const group = Number(activeGroup?.getAttribute('data-group') ?? '4');
    const activeDrinkButton = this.querySelector('[data-type][aria-pressed="true"], [data-category][aria-pressed="true"]');
    const selectedDrinkType = activeDrinkButton?.getAttribute('data-type') || activeDrinkButton?.getAttribute('data-category') || 'all';
    const budgetSelect = this.querySelector('[data-budget]');
    /** @type {Budget} */
    const budget = budgetSelect instanceof HTMLSelectElement ? /** @type {Budget} */ (budgetSelect.value) : 'all';
    const cards = this.querySelectorAll('[data-bundle-card]');
    let matched = 0;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const participant = Number(card.dataset.participant);
      const min = Number(card.dataset.groupMin);
      const max = Number(card.dataset.groupMax);
      const price = Number(card.dataset.price);
      const groupMatch = Number.isFinite(participant) && participant > 0
        ? group === participant
        : (Number.isFinite(min) && Number.isFinite(max) && group >= Math.min(min, max) && group <= Math.max(min, max));
      const categoryMatch = this.#matchesDrinkType(card, selectedDrinkType);
      const budgetMatch = this.#matchesBudget(price, budget);
      const matches = groupMatch && categoryMatch && budgetMatch;
      if (matches) matched += 1;
      card.hidden = !matches;
    }

    const empty = this.querySelector('[data-empty]');
    if (empty instanceof HTMLElement) empty.hidden = matched > 0;
    const count = this.querySelector('[data-result-count]');
    if (count instanceof HTMLElement) count.textContent = `${matched} ${matched === 1 ? 'bundle' : 'bundles'} match your choices.`;
  }

  /**
   * @param {HTMLElement} card
   * @param {string} selectedFilter
   * @returns {boolean}
   */
  #matchesDrinkType(card, selectedFilter) {
    if (!selectedFilter || selectedFilter === 'all') return true;
    const filter = selectedFilter.toLowerCase().trim();
    if (!filter || filter === 'all') return true;

    const cardCategory = (card.dataset.category || '').toLowerCase().trim();
    const cardType = (card.dataset.type || '').toLowerCase().trim();

    if (cardCategory === filter || cardType === filter) return true;

    const normalize = (str) => str.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const filterNorm = normalize(filter);
    if (filterNorm && (normalize(cardCategory) === filterNorm || normalize(cardType) === filterNorm)) {
      return true;
    }

    if (cardType && filter.length >= 3 && cardType.includes(filter)) return true;
    if (cardCategory && filter.length >= 3 && cardCategory.includes(filter)) return true;

    if (cardType && cardType.length >= 3 && filter.includes(cardType)) return true;
    if (cardCategory && cardCategory.length >= 3 && filter.includes(cardCategory)) return true;

    return false;
  }

  /** @param {number} price @param {Budget} budget @returns {boolean} */
  #matchesBudget(price, budget) {
    if (!Number.isFinite(price)) return false;
    const configuredLow = Number(this.dataset.budgetLow);
    const configuredMid = Number(this.dataset.budgetMid);
    const configuredHigh = Number(this.dataset.budgetHigh);
    const validBoundaries = Number.isFinite(configuredLow) && configuredLow > 0
      && Number.isFinite(configuredMid) && configuredMid > configuredLow
      && Number.isFinite(configuredHigh) && configuredHigh > configuredMid;
    const low = validBoundaries ? configuredLow : 25000000;
    const mid = validBoundaries ? configuredMid : 50000000;
    const high = validBoundaries ? configuredHigh : 100000000;
    switch (budget) {
      case 'under250': return price < low;
      case '250to500': return price >= low && price < mid;
      case '500to1000': return price >= mid && price <= high;
      case 'over1000': return price > high;
      default: return true;
    }
  }

  /** @param {HTMLFormElement} form */
  async #addToCart(form) {
    const button = form.querySelector('[data-add-button]');
    const status = form.querySelector('[data-form-status]');
    const label = form.querySelector('[data-button-label]');
    const variantInput = form.querySelector('input[name="id"]');
    if (!(button instanceof HTMLButtonElement) || !(status instanceof HTMLElement) || !(variantInput instanceof HTMLInputElement)) return;
    if (button.disabled || !/^[1-9]\d*$/.test(variantInput.value)) return;

    const originalLabel = label?.textContent ?? '';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = '';
    if (label) label.textContent = 'Adding…';

    try {
      const theme = /** @type {{ routes?: { cart_add_url?: string, cart_url?: string } } | undefined} */ (
        typeof Theme === 'undefined' ? undefined : Theme
      );
      const addUrl = theme?.routes?.cart_add_url ?? `${form.action.replace(/\/$/, '')}.js`;
      const cartUrl = theme?.routes?.cart_url ?? '/cart';
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((item) => item.getAttribute('data-section-id'))
        .filter((id) => id !== null && id.length > 0);
      const data = new FormData(form);
      if (sectionIds.length > 0) data.set('sections', [...new Set(sectionIds)].join(','));

      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: data,
      });
      /** @type {{ status?: number, message?: string, description?: string, sections?: Record<string, string> }} */
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This item could not be added.');

      const cartResponse = await fetch(`${cartUrl.replace(/\/$/, '')}.js`, { credentials: 'same-origin' });
      if (!cartResponse.ok) {
        window.location.assign(cartUrl);
        return;
      }
      /** @type {{ item_count?: number }} */
      const cart = await cartResponse.json();
      if (typeof cart.item_count !== 'number') {
        window.location.assign(cartUrl);
        return;
      }

      this.dispatchEvent(new CartAddEvent({}, form.id || variantInput.value, {
        source: 'party-bundles',
        itemCount: cart.item_count,
        productId: button.dataset.productId,
        sections: result.sections,
      }));
      status.textContent = 'Added to cart.';
      if (label) label.textContent = 'Added to Cart';
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      if (label) label.textContent = originalLabel;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label && label.textContent === 'Added to Cart') {
        window.setTimeout(() => { label.textContent = originalLabel; }, 2000);
      }
    }
  }

  #openChat() {
    const status = this.querySelector('[data-ai-status]');

    const findAndOpenChat = () => {
      // Specifically target the Chat launcher/window iframe, ignoring the popup iframe
      const chatIframe = document.querySelector(
        '#flyweight-chat iframe[title="Chat"], iframe[title="Chat"]'
      ) || Array.from(document.querySelectorAll('iframe')).find(
        (f) => f.title === 'Chat' || f.getAttribute('data-testid') === 'chat-overlay'
      );

      if (chatIframe) {
        try {
          const doc = chatIframe.contentDocument || chatIframe.contentWindow?.document;
          if (doc) {
            // If chat window is already open, focus its composer textarea/input
            const composer = doc.querySelector('textarea, input[type="text"], input');
            if (composer && typeof composer.focus === 'function') {
              composer.focus();
              if (status) status.textContent = '';
              return true;
            }

            // Find the launcher button inside the chat iframe
            const button = doc.querySelector(
              '#chat-button, button.chat-button, [data-testid="chat-button"], button[aria-label*="KAPITALL" i], button[title*="KAPITALL" i], button[aria-label*="Chat" i]'
            );

            if (button && typeof button.click === 'function') {
              if (typeof button.focus === 'function') button.focus();
              button.click();

              if (status) status.textContent = '';

              // Focus input once window opens
              let attempts = 0;
              const checkInputInterval = window.setInterval(() => {
                attempts += 1;
                try {
                  const updatedDoc = chatIframe.contentDocument || chatIframe.contentWindow?.document;
                  const input = updatedDoc?.querySelector('textarea, input[type="text"], input');
                  if (input && typeof input.focus === 'function') {
                    input.focus();
                    window.clearInterval(checkInputInterval);
                  } else if (attempts >= 25) {
                    window.clearInterval(checkInputInterval);
                  }
                } catch (_) {
                  window.clearInterval(checkInputInterval);
                }
              }, 100);

              return true;
            }
          }
        } catch (_) {}
      }

      // Fallback for direct DOM buttons if rendered outside iframe
      const directButton = document.querySelector(
        '#flyweight-chat button.chat-button, [data-flyweight-chat] button.chat-button, button.chat-button'
      );
      if (directButton && typeof directButton.click === 'function') {
        if (typeof directButton.focus === 'function') directButton.focus();
        directButton.click();
        if (status) status.textContent = '';
        return true;
      }

      return false;
    };

    if (findAndOpenChat()) return;

    // If chat widget script is still loading/initializing, retry briefly (up to 3 seconds)
    if (status) status.textContent = 'Opening Ask Kapitall AI...';
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (findAndOpenChat() || attempts >= 30) {
        window.clearInterval(interval);
        if (attempts >= 30 && status) {
          status.textContent = 'The Kapitall AI chat is unavailable right now.';
        }
      }
    }, 100);
  }
}

if (!customElements.get('party-bundles')) customElements.define('party-bundles', PartyBundles);
